import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { diffLines } from 'diff';
import { AlertCircle, AlertTriangle, CheckCircle2, Plus, Trash2, Wand2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { JsonEditor, PromptEditor } from '@/components/PromptEditor';
import { Badge, Button, Card, Dialog, ErrorNote, Field, Input, Select, Textarea, Toggle } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { extractVariables } from '@/lib/format';
import { canEdit, rk, useAgents, useMe, useModels, useSnippets, useTools } from '@/lib/hooks';
import type { AgentConfig, AgentDetail, ConfigIssue, FlowConfig, ReasoningEffort, Version } from '@/lib/types';
import { isFlow } from '@/lib/types';

const pretty = (v: unknown) => JSON.stringify(v, null, 2);

/** Stable, readable text form of a config for diffs (system prompt kept as real lines). */
export const diffText = (c: AgentConfig | FlowConfig | undefined) => {
  if (!c) return '';
  if (isFlow(c)) return pretty(c);
  const { system, userTemplate, ...rest } = c;
  return `## System prompt\n${system}\n\n## User template\n${userTemplate ?? ''}\n\n## Settings\n${pretty(rest)}\n`;
};

export const AgentEditor = ({ detail }: { detail: AgentDetail }) => {
  const me = useMe();
  const editable = canEdit(me.data) && !detail.agent.archivedAt;
  const qc = useQueryClient();
  const draft = detail.versions.find((v) => v.status === 'draft');
  const live = detail.versions.find((v) => v.id === detail.agent.publishedVersionId);
  const source: Version | undefined = draft ?? live ?? detail.versions[0];

  const [config, setConfig] = useState<AgentConfig>(() => (source?.config as AgentConfig | undefined) ?? ({ model: { provider: 'openai', name: 'gpt-5.6-luna' }, system: '' } as AgentConfig));
  const [changelog, setChangelog] = useState(draft?.changelog ?? '');
  const [dirty, setDirty] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Reset local state when the server draft changes identity (discard/publish).
  const sourceId = source?.id;
  useEffect(() => {
    if (source) {
      setConfig(source.config as AgentConfig);
      setChangelog(draft?.changelog ?? '');
      setDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  const update = (patch: Partial<AgentConfig>) => {
    setConfig((c) => ({ ...c, ...patch }));
    setDirty(true);
  };

  // Live validation (debounced) so problems show while typing, not only at publish.
  const [debounced, setDebounced] = useState(config);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(config), 600);
    return () => clearTimeout(t);
  }, [config]);
  const validation = useQuery({
    queryKey: rk('validate', detail.agent.id, debounced),
    queryFn: () => api<{ issues: ConfigIssue[] }>('/v1/validate', { body: { config: debounced, slug: detail.agent.slug } }),
    placeholderData: (prev) => prev,
  });
  const issues = validation.data?.issues ?? [];
  const errors = issues.filter((i) => i.level === 'error');

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: rk('agent', detail.agent.id) });
    void qc.invalidateQueries({ queryKey: rk('agents') });
  };

  const save = useMutation({
    mutationFn: () => api<{ version: Version; issues: ConfigIssue[] }>(`/v1/agents/${detail.agent.id}/draft`, { method: 'PUT', body: { config, changelog } }),
    onSuccess: (res) => {
      setDirty(false);
      invalidate();
      toast.success(`Draft v${res.version.version} saved`);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const discard = useMutation({
    mutationFn: () => api(`/v1/agents/${detail.agent.id}/draft`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidate();
      toast.success('Draft discarded');
    },
  });

  // ⌘S / Ctrl+S saves the draft.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (editable) saveRef.current.mutate();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editable]);

  const status = draft ? (dirty ? 'unsaved changes' : `draft v${draft.version}`) : dirty ? 'unsaved changes (new draft)' : live ? `viewing live v${live.version}` : 'no versions';

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
      <div className="flex min-w-0 flex-col gap-5">
        <Card
          title="Prompt"
          actions={
            <span className="flex items-center gap-2 text-xs text-ink-3">
              {editable && <InsertSnippet onInsert={(slug) => update({ system: `${config.system.replace(/\s*$/, '')}\n\n{{> ${slug}}}` })} />}
              <Badge tone={dirty ? 'warn' : draft ? 'brand' : 'neutral'}>{status}</Badge>
            </span>
          }
        >
          <div className="flex flex-col gap-4 p-4">
            <Field label="System prompt" hint="{{variable}}, {{#if variable}}…{{/if}}">
              <PromptEditor value={config.system} onChange={(v) => update({ system: v })} readOnly={!editable} minHeight="280px" />
            </Field>
            <Field label="User message template" hint="Optional — appended as the user turn on every run">
              <PromptEditor value={config.userTemplate ?? ''} onChange={(v) => update({ userTemplate: v || undefined })} readOnly={!editable} minHeight="80px" placeholder={'e.g.\nCV:\n{{cvText}}'} />
            </Field>
          </div>
        </Card>
        <VariablesCard config={config} update={update} editable={editable} />
        <OutputCard config={config} update={update} editable={editable} />
      </div>

      <div className="flex min-w-0 flex-col gap-5">
        <Card title="Save & publish">
          <div className="flex flex-col gap-3 p-4">
            <Field label="Change note">
              <Textarea rows={2} value={changelog} disabled={!editable} onChange={(e) => (setChangelog(e.target.value), setDirty(true))} placeholder="What changed and why" />
            </Field>
            <IssuesList issues={issues} loading={validation.isFetching} />
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" disabled={!editable || !dirty} loading={save.isPending} onClick={() => save.mutate()} title="⌘S">
                Save draft
              </Button>
              <Button variant="primary" disabled={!editable || !draft || dirty || errors.length > 0} onClick={() => setPublishing(true)} title={dirty ? 'Save first' : undefined}>
                Publish…
              </Button>
              {draft && (
                <Button variant="ghost" disabled={!editable} loading={discard.isPending} onClick={() => confirm('Discard this draft?') && discard.mutate()}>
                  Discard
                </Button>
              )}
            </div>
            {!editable && <p className="text-xs text-ink-3">{detail.agent.archivedAt ? 'Archived agents are read-only.' : 'You have view access.'}</p>}
          </div>
        </Card>
        <ModelCard config={config} update={update} editable={editable} />
        <ToolsCard config={config} update={update} editable={editable} project={detail.agent.project} slug={detail.agent.slug} />
        <Card title="Advanced">
          <div className="flex flex-col gap-3 p-4">
            <Field label="Max turns" hint="Model calls per run (tool loops, handoffs)">
              <Input type="number" min={1} max={50} disabled={!editable} value={config.maxTurns ?? 10} onChange={(e) => update({ maxTurns: Number(e.target.value) || undefined })} />
            </Field>
            <Toggle
              disabled={!editable}
              checked={config.logPayloads !== false}
              onChange={(v) => update({ logPayloads: v })}
              label="Log prompts and outputs in traces"
            />
            <p className="text-xs text-ink-3">Turn off for agents that see sensitive data. Tokens, latency and cost are still tracked.</p>
          </div>
        </Card>
      </div>

      <PublishDialog open={publishing} onClose={() => setPublishing(false)} detail={detail} draft={draft} live={live} changelog={changelog} onPublished={invalidate} />
    </div>
  );
};

/** Appends {{> slug}} to the system prompt; only published snippets are offered. */
const InsertSnippet = ({ onInsert }: { onInsert: (slug: string) => void }) => {
  const q = useSnippets();
  const available = (q.data?.snippets ?? []).filter((s) => s.publishedContent !== null);
  if (!available.length) return null;
  return (
    <Select
      className="h-7 w-44 text-xs"
      value=""
      onChange={(e) => {
        if (e.target.value) onInsert(e.target.value);
      }}
    >
      <option value="">Insert snippet…</option>
      {available.map((s) => (
        <option key={s.id} value={s.slug}>
          {s.name}
        </option>
      ))}
    </Select>
  );
};

const IssuesList = ({ issues, loading }: { issues: ConfigIssue[]; loading: boolean }) => {
  if (!issues.length)
    return (
      <p className={cn('flex items-center gap-1.5 text-xs text-ok', loading && 'opacity-50')}>
        <CheckCircle2 className="size-3.5" /> No problems found
      </p>
    );
  return (
    <ul className={cn('flex flex-col gap-1.5', loading && 'opacity-60')}>
      {issues.map((i, n) => (
        <li key={n} className={cn('flex gap-1.5 text-xs', i.level === 'error' ? 'text-bad' : 'text-warn')}>
          {i.level === 'error' ? <AlertCircle className="mt-px size-3.5 shrink-0" /> : <AlertTriangle className="mt-px size-3.5 shrink-0" />}
          <span>
            {i.message}
            {i.path && <span className="mono ml-1 text-ink-3">{i.path}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
};

type CardProps = { config: AgentConfig; update: (p: Partial<AgentConfig>) => void; editable: boolean };

const ModelCard = ({ config, update, editable }: CardProps) => {
  const models = useModels();
  const all = (models.data?.models ?? []).filter((m) => m.enabled || (m.provider === config.model.provider && m.name === config.model.name));
  const providers = [...new Set(all.map((m) => m.provider))];
  const current = all.find((m) => m.provider === config.model.provider && m.name === config.model.name);
  const caps = current?.capabilities;
  const hasTools = (config.tools?.length ?? 0) + (config.subAgents?.length ?? 0) + (config.handoffs?.length ?? 0) > 0;
  const setModel = (patch: Partial<AgentConfig['model']>) => update({ model: { ...config.model, ...patch } });

  return (
    <Card title="Model">
      <div className="grid grid-cols-2 gap-3 p-4">
        <Field label="Provider">
          <Select
            disabled={!editable}
            value={config.model.provider}
            onChange={(e) => {
              const provider = e.target.value as AgentConfig['model']['provider'];
              const first = all.find((m) => m.provider === provider);
              setModel({ provider, name: first?.name ?? '', reasoningEffort: undefined, temperature: undefined });
            }}
          >
            {providers.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </Select>
        </Field>
        <Field label="Model">
          <Select disabled={!editable} value={config.model.name} onChange={(e) => setModel({ name: e.target.value })}>
            {all
              .filter((m) => m.provider === config.model.provider)
              .map((m) => (
                <option key={m.name} value={m.name}>
                  {m.label ?? m.name}
                </option>
              ))}
            {!current && <option value={config.model.name}>{config.model.name} (not in registry)</option>}
          </Select>
        </Field>
        <Field label="Max output tokens">
          <Input type="number" min={1} disabled={!editable} value={config.model.maxTokens ?? ''} placeholder="provider default" onChange={(e) => setModel({ maxTokens: e.target.value ? Number(e.target.value) : undefined })} />
        </Field>
        {caps && caps.reasoningEfforts.length > 0 ? (
          <Field label="Reasoning effort" hint={hasTools && caps.toolsRequireReasoningNone ? 'forced to none (tools)' : undefined}>
            <Select
              disabled={!editable || (hasTools && caps.toolsRequireReasoningNone)}
              value={hasTools && caps.toolsRequireReasoningNone ? 'none' : (config.model.reasoningEffort ?? '')}
              onChange={(e) => setModel({ reasoningEffort: (e.target.value || undefined) as ReasoningEffort | undefined })}
            >
              <option value="">default</option>
              {caps.reasoningEfforts.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </Select>
          </Field>
        ) : (
          <div />
        )}
        {caps?.temperature && (
          <Field label="Temperature">
            <Input type="number" step={0.1} min={0} max={2} disabled={!editable} value={config.model.temperature ?? ''} placeholder="default" onChange={(e) => setModel({ temperature: e.target.value === '' ? undefined : Number(e.target.value) })} />
          </Field>
        )}
      </div>
      {current && (current.pricing?.input == null || current.pricing?.output == null) && (
        <p className="px-4 pb-3 text-xs text-warn">No price set for this model — cost won't be tracked. An admin can add it under Models.</p>
      )}
    </Card>
  );
};

const VariablesCard = ({ config, update, editable }: CardProps) => {
  const vars = config.variables ?? [];
  const used = useMemo(() => extractVariables(`${config.system}\n${config.userTemplate ?? ''}`), [config.system, config.userTemplate]);
  const missing = used.filter((u) => !vars.some((v) => v.name === u));
  const setVars = (next: typeof vars) => update({ variables: next });

  return (
    <Card
      title="Variables"
      actions={
        editable && (
          <>
            {missing.length > 0 && (
              <Button size="sm" variant="secondary" onClick={() => setVars([...vars, ...missing.map((name) => ({ name, required: false }))])}>
                <Wand2 className="size-3.5" /> Add {missing.length} from prompts
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setVars([...vars, { name: '', required: false }])}>
              <Plus className="size-3.5" /> Add
            </Button>
          </>
        )
      }
    >
      {vars.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-ink-3">Values the calling code passes in (e.g. cvText, jobTitle). Required ones fail the run early when missing.</p>
      ) : (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] tracking-wide text-ink-3 uppercase">
              <th className="px-4 py-1.5 font-medium">Name</th>
              <th className="px-2 py-1.5 font-medium">Description</th>
              <th className="px-2 py-1.5 font-medium">Required</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {vars.map((v, i) => (
              <tr key={i} className="border-t border-line-2">
                <td className="px-4 py-1.5">
                  <Input className="mono h-7" disabled={!editable} value={v.name} onChange={(e) => setVars(vars.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                  {!used.includes(v.name) && v.name && <span className="text-[11px] text-warn">not used in prompts</span>}
                </td>
                <td className="px-2 py-1.5">
                  <Input className="h-7" disabled={!editable} value={v.description ?? ''} onChange={(e) => setVars(vars.map((x, j) => (j === i ? { ...x, description: e.target.value || undefined } : x)))} />
                </td>
                <td className="px-2 py-1.5">
                  <input type="checkbox" className="accent-ink" disabled={!editable} checked={Boolean(v.required)} onChange={(e) => setVars(vars.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))} />
                </td>
                <td className="px-2 py-1.5 text-right">
                  {editable && (
                    <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => setVars(vars.filter((_, j) => j !== i))} title="Remove">
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
};

const OutputCard = ({ config, update, editable }: CardProps) => {
  const out = config.output ?? { type: 'text' as const };
  const [schemaText, setSchemaText] = useState(out.type === 'json_schema' ? pretty(out.schema) : '{\n  "type": "object",\n  "properties": {},\n  "required": [],\n  "additionalProperties": false\n}');
  const [schemaError, setSchemaError] = useState<string | null>(null);

  return (
    <Card title="Output">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap gap-4">
          {(
            [
              ['text', 'Text'],
              ['json_object', 'JSON (free-form)'],
              ['json_schema', 'JSON (schema-enforced)'],
            ] as const
          ).map(([t, label]) => (
            <label key={t} className="flex items-center gap-1.5 text-[13px]">
              <input
                type="radio"
                className="accent-ink"
                disabled={!editable}
                checked={out.type === t}
                onChange={() => {
                  if (t === 'json_schema') {
                    try {
                      update({ output: { type: 'json_schema', name: 'output', schema: JSON.parse(schemaText), strict: true } });
                    } catch {
                      update({ output: { type: 'json_schema', name: 'output', schema: { type: 'object', properties: {}, required: [], additionalProperties: false }, strict: true } });
                    }
                  } else update({ output: { type: t } });
                }}
              />
              {label}
            </label>
          ))}
        </div>
        {out.type === 'json_schema' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Schema name">
                <Input className="mono" disabled={!editable} value={out.name ?? ''} onChange={(e) => update({ output: { ...out, name: e.target.value } })} />
              </Field>
              <div className="flex items-end pb-2">
                <Toggle disabled={!editable} checked={out.strict !== false} onChange={(v) => update({ output: { ...out, strict: v } })} label="Strict" />
              </div>
            </div>
            <Field label="JSON schema">
              <JsonEditor
                readOnly={!editable}
                value={schemaText}
                onChange={(v) => {
                  setSchemaText(v);
                  try {
                    update({ output: { ...out, schema: JSON.parse(v) } });
                    setSchemaError(null);
                  } catch (e) {
                    setSchemaError((e as Error).message);
                  }
                }}
              />
            </Field>
            {schemaError && <p className="text-xs text-bad">Invalid JSON: {schemaError}</p>}
          </>
        )}
      </div>
    </Card>
  );
};

const ToolsCard = ({ config, update, editable, project, slug }: CardProps & { project: string; slug: string }) => {
  const tools = useTools();
  const agents = useAgents();
  const registered = (tools.data?.manifests ?? []).filter((m) => m.project === project).flatMap((m) => m.tools);
  const selected = config.tools ?? [];
  const unknown = selected.filter((t) => !registered.some((r) => r.name === t));
  const others = (agents.data?.agents ?? []).filter((a) => a.slug !== slug && a.publishedVersion);
  const subs = config.subAgents ?? [];
  const hands = config.handoffs ?? [];

  return (
    <Card title="Tools & other agents">
      <div className="flex flex-col gap-4 p-4">
        <div>
          <div className="mb-1.5 text-xs font-medium text-ink-2">Code tools</div>
          {registered.length === 0 && unknown.length === 0 && (
            <p className="text-xs text-ink-3">No tools registered by the {project} host yet. They appear after the product code calls registerTools and runs once.</p>
          )}
          <div className="flex flex-col gap-1.5">
            {registered.map((t) => (
              <label key={t.name} className="flex items-start gap-2 text-[13px]">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-ink"
                  disabled={!editable}
                  checked={selected.includes(t.name)}
                  onChange={(e) => update({ tools: e.target.checked ? [...selected, t.name] : selected.filter((x) => x !== t.name) })}
                />
                <span>
                  <span className="mono">{t.name}</span>
                  <span className="block text-xs text-ink-3">{t.description}</span>
                </span>
              </label>
            ))}
            {unknown.map((t) => (
              <label key={t} className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" className="accent-ink" disabled={!editable} checked onChange={() => update({ tools: selected.filter((x) => x !== t) })} />
                <span className="mono">{t}</span> <Badge tone="warn">not registered</Badge>
              </label>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-medium text-ink-2">Agents as tools</span>
            {editable && others.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => update({ subAgents: [...subs, { slug: others[0].slug, toolName: others[0].slug.split('.').pop()!.replace(/[^a-zA-Z0-9_-]/g, '_'), description: others[0].description || others[0].name }] })}>
                <Plus className="size-3.5" /> Add
              </Button>
            )}
          </div>
          {subs.length === 0 && <p className="text-xs text-ink-3">Let this agent call another agent and use its answer.</p>}
          {subs.map((s, i) => (
            <div key={i} className="mb-2 flex flex-col gap-1.5 rounded-md border border-line p-2">
              <div className="flex gap-1.5">
                <Select className="h-7" disabled={!editable} value={s.slug} onChange={(e) => update({ subAgents: subs.map((x, j) => (j === i ? { ...x, slug: e.target.value } : x)) })}>
                  {others.map((a) => (
                    <option key={a.slug}>{a.slug}</option>
                  ))}
                  {!others.some((a) => a.slug === s.slug) && <option>{s.slug}</option>}
                </Select>
                {editable && (
                  <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => update({ subAgents: subs.filter((_, j) => j !== i) })}>
                    <Trash2 className="size-3.5" />
                  </button>
                )}
              </div>
              <Input className="mono h-7" placeholder="tool_name" disabled={!editable} value={s.toolName} onChange={(e) => update({ subAgents: subs.map((x, j) => (j === i ? { ...x, toolName: e.target.value } : x)) })} />
              <Input className="h-7" placeholder="When should it be used?" disabled={!editable} value={s.description} onChange={(e) => update({ subAgents: subs.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })} />
            </div>
          ))}
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-medium text-ink-2">Handoffs</span>
            {editable && others.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => update({ handoffs: [...hands, { slug: others[0].slug, description: '' }] })}>
                <Plus className="size-3.5" /> Add
              </Button>
            )}
          </div>
          {hands.length === 0 && <p className="text-xs text-ink-3">Hand the conversation over to a specialist agent, which then owns the next turns.</p>}
          {hands.map((h, i) => (
            <div key={i} className="mb-2 flex flex-col gap-1.5 rounded-md border border-line p-2">
              <div className="flex gap-1.5">
                <Select className="h-7" disabled={!editable} value={h.slug} onChange={(e) => update({ handoffs: hands.map((x, j) => (j === i ? { ...x, slug: e.target.value } : x)) })}>
                  {others.map((a) => (
                    <option key={a.slug}>{a.slug}</option>
                  ))}
                  {!others.some((a) => a.slug === h.slug) && <option>{h.slug}</option>}
                </Select>
                {editable && (
                  <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => update({ handoffs: hands.filter((_, j) => j !== i) })}>
                    <Trash2 className="size-3.5" />
                  </button>
                )}
              </div>
              <Input className="h-7" placeholder="When to hand off (optional)" disabled={!editable} value={h.description ?? ''} onChange={(e) => update({ handoffs: hands.map((x, j) => (j === i ? { ...x, description: e.target.value || undefined } : x)) })} />
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

const PublishDialog = ({
  open,
  onClose,
  detail,
  draft,
  live,
  changelog,
  onPublished,
}: {
  open: boolean;
  onClose: () => void;
  detail: AgentDetail;
  draft?: Version;
  live?: Version;
  changelog: string;
  onPublished: () => void;
}) => {
  const [note, setNote] = useState(changelog);
  useEffect(() => setNote(changelog), [changelog, open]);
  const parts = useMemo(() => (draft ? diffLines(diffText(live?.config), diffText(draft.config)) : []), [draft, live]);
  const publish = useMutation({
    mutationFn: () => api<{ warnings: ConfigIssue[] }>(`/v1/agents/${detail.agent.id}/versions/${draft!.id}/publish`, { body: { changelog: note } }),
    onSuccess: () => {
      toast.success(`v${draft!.version} is live — hosts pick it up within 30 seconds`);
      onPublished();
      onClose();
    },
  });
  const apiIssues = publish.error instanceof ApiError ? (publish.error.details?.issues as ConfigIssue[] | undefined) : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      wide
      title={`Publish v${draft?.version ?? ''}${live ? ` (replaces live v${live.version})` : ''}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={publish.isPending} onClick={() => publish.mutate()}>
            Publish
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Change note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed and why" />
        </Field>
        <div>
          <div className="mb-1 text-xs font-medium text-ink-2">{live ? `Changes since v${live.version}` : 'First version'}</div>
          <pre className="max-h-[50vh] overflow-auto rounded-md border border-line text-[12px] leading-relaxed">
            {parts.map((p, i) => (
              <span key={i} className={cn('block whitespace-pre-wrap px-3', p.added && 'bg-ok-soft text-ok', p.removed && 'bg-bad-soft text-bad line-through decoration-bad/40', !p.added && !p.removed && 'text-ink-3')}>
                {p.value
                  .replace(/\n$/, '')
                  .split('\n')
                  .map((l) => `${p.added ? '+ ' : p.removed ? '- ' : '  '}${l}`)
                  .join('\n')}
              </span>
            ))}
          </pre>
        </div>
        {publish.error ? <ErrorNote error={publish.error} /> : null}
        {apiIssues && <IssuesList issues={apiIssues} loading={false} />}
      </div>
    </Dialog>
  );
};
